import React, { useMemo } from 'react';
import DynamicsDetailPage from '../DynamicsDetailPage';
import { getApiV1Url } from '../config/api';
import axios from 'axios';
import { Certif } from '../CertificatesPage';

interface DynamicsApplicantsPageProps {
    filteredCertif?: Certif[];
}

const DynamicsApplicantsPage: React.FC<DynamicsApplicantsPageProps> = ({ filteredCertif: propFilteredCertif }) => {
    const [certif, setCertif] = React.useState<Certif[]>([]);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        if (propFilteredCertif !== undefined) {
            setCertif(propFilteredCertif);
            setLoading(false);
        }
    }, [propFilteredCertif]);
    
    React.useEffect(() => {
        if (propFilteredCertif !== undefined) {
            return;
        }
        
        const fetchData = async () => {
            try {
                const access = localStorage.getItem('access');
                const response = await axios.get<Certif[]>(getApiV1Url('CERTIFICATES'), {
                    headers: { Authorization: `Bearer ${access}` },
                });
                setCertif(response.data);
            } catch (error) {
                console.error('Error fetching certificates:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const filteredCertif = propFilteredCertif !== undefined ? propFilteredCertif : certif;

    const applicantMaps = useMemo(() => {
        const nameToInn = new Map<string, string>();
        const innToName = new Map<string, string>();
        const innToRegion = new Map<string, string>();
        const nameToRegion = new Map<string, string>();
        const normalize = (s: string) => s.replace(/\s+/g, ' ').trim();
        for (const item of filteredCertif) {
            const inn = normalize(item.applicant_inn || '');
            if (!inn) continue;
            const full = normalize(item.applicant_fullname || '');
            const short = normalize(item.applicant_shortname || '');
            const name = short || full;
            if (!name || name.toLowerCase() === 'отсутствует' || /^\d{10,}$/.test(name)) continue;
            if (!innToName.has(inn)) innToName.set(inn, name);
            if (!nameToInn.has(name)) nameToInn.set(name, inn);
            const region = normalize(item.applicant_region || '');
            if (region) {
                if (!innToRegion.has(inn)) innToRegion.set(inn, region);
                if (!nameToRegion.has(name)) nameToRegion.set(name, region);
            }
        }
        return { nameToInn, innToName, innToRegion, nameToRegion };
    }, [filteredCertif]);

    const getDynamicsData = useMemo(() => {
        return () => {
            const applicantCounts = new Map<string, Map<string, number>>();
            filteredCertif.forEach(item => {
                if (!item.cert_reg_data) return;
                const year = String(item.cert_reg_data.substring(0, 4));
                if (!applicantCounts.has(year)) applicantCounts.set(year, new Map());
                const m = applicantCounts.get(year)!;
                const inn = (item.applicant_inn || '').trim();
                if (!inn) return;
                const shortname = (item.applicant_shortname || '').trim();
                const applicantKey = shortname ? `${inn} - ${shortname}` : inn;
                m.set(applicantKey, (m.get(applicantKey) || 0) + 1);
            });
            const applicantNames = Array.from(new Set(Array.from(applicantCounts.values()).flatMap(m => Array.from(m.keys())))).sort();
            const years = Array.from(new Set(filteredCertif.map(i => i.cert_reg_data ? i.cert_reg_data.substring(0,4) : '').filter(Boolean))).sort();
            return { years, names: applicantNames, counts: applicantCounts };
        };
    }, [filteredCertif]);

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                Загрузка...
            </div>
        );
    }

    return (
        <DynamicsDetailPage
            getDynamicsData={getDynamicsData}
            title="Заявители - Динамика"
            chartTitle="Динамика сертификатов по заявителям"
            maps={applicantMaps}
        />
    );
};

export default DynamicsApplicantsPage;

